import { trimEnd } from "lodash-es"
import { toast } from "sonner"

import { DEV_MODE_PREFERENCE_KEY } from "@/hooks/useDevMode.ts"
import { ApiService } from "@/lib/client/ApiService.ts"
import { AiAgentSpaceApiClient } from "@/lib/client/generated/AiAgentSpaceApiClient.ts"
import { AiChatApiClient } from "@/lib/client/generated/AiChatApiClient.ts"
import { CodeViewServiceClient } from "@/lib/client/generated/CodeViewServiceClient.ts"
import { InitProjectApiClient } from "@/lib/client/generated/InitProjectApiClient.ts"
import { InitRuntimeConfigApiClient } from "@/lib/client/generated/InitRuntimeConfigApiClient.ts"
import { InvitationsClient } from "@/lib/client/generated/InvitationsClient.ts"
import { PreviewApiClient } from "@/lib/client/generated/PreviewApiClient.ts"
import { ProfilesClient } from "@/lib/client/generated/ProfilesClient.ts"
import { ProjectsClient } from "@/lib/client/generated/ProjectsClient.ts"
import { SshKeysClient } from "@/lib/client/generated/SshKeysClient.ts"
import {
  ConnectionStatus,
  OrgNotFoundException,
  ProjectNotFoundException,
  TaskNotFoundException,
  UserInOrgNotFoundException
} from "@/lib/client/types.ts"
import { TokenInfo } from "@/lib/client/WebSocketStableConnection.ts"
import { FigmaSecureCookie, setSecureStateCookie, SlackSecureCookie } from "@/lib/cookieUtils.ts"
import { getLogger } from "@/lib/logging.ts"

import type { GenericTaskParams } from "@/lib/apiClient/types.ts"
import type { AppDateTime, InitializedChannel, Pair } from "@/lib/client/api-types.ts"
import type {
  AiAgentTask,
  AiAgentTaskContextItem,
  AiAgentTaskDetails,
  AiAgentTaskDomElementIn,
  AiAgentTaskEvent,
  AiAgentTaskSettings,
  AiChatEvent,
  AiChatMessage,
  AiChatMessageAttachment,
  AvailableLog,
  Batch,
  BatchInfo,
  BranchInfo,
  CPrincipal,
  DevTaskStats,
  ExecCommandResult,
  ExecCommandResultUpdated,
  FigmaStatus,
  FileContentPart,
  GitCommitChange,
  GitCommitFileRequest,
  GitCommitResult,
  GitFile,
  GitHubPrInfo,
  GitHubStatus,
  GrazieLLMProfileDTO,
  GrazieLLMRequest,
  InitRuntimeConfigTaskState,
  InlineDiff,
  InvitationLandingContext,
  ListFilesOptions,
  LLMLogEntry,
  LLMLogPage,
  LlmQuota,
  MessagesSorting,
  OrganizationMember,
  OrganizationMemberRole,
  OrganizationPermissions,
  PR_Project2,
  PrCreationResult,
  ProfileIdentifier,
  ProjectCodingGuidelines,
  ProjectColorKey,
  ProjectCreationBreadcrumb,
  ProjectKey,
  ProjectPermissions,
  ProjectSecret,
  ProjectSecretWithValue,
  ProjectSettings,
  ProjectTeamRole,
  RemoteUrlValidationResult,
  RepositoryAuthType,
  RepositoryFetchStatus,
  RuntimeConfig,
  SshKeyData,
  SshKeyReportData,
  TaskCheckpointCheckoutResult,
  TD_MemberProfile,
  TID,
  TypingEventDTO,
  UPreviewProcessesApiState,
  UProfileIdentifier,
  URemoteRepositoryAuth,
  UTaskCheckpointCheckoutResult,
  WarmupInfo,
  WarmupSnapshot
} from "@/lib/client/generated/TypeDefinitions.ts"
import type { TokenSource } from "@/lib/client/WebSocketStableConnection.ts"
import type { UserInfo } from "@/lib/oauth/AuthController.ts"
import type { Observable } from "rxjs"

const log = getLogger("ApiClient")

export interface ApiClient {
  addPreviewCommand(taskId: string, id: string, title: string, script: string): Promise<void>

  addProjectSecret(projectKey: ProjectKey, secret: ProjectSecret, value: string): Promise<void>

  addSshKey(profile: UProfileIdentifier, key: string, comment: string): Promise<void>

  archivedMessages(taskId: string, batchInfo: BatchInfo, blockMessageIds: string[], sorting: MessagesSorting): Promise<Batch<AiChatMessage>>

  buildStaticUrl(path: string): string

  canSshToPreviewEnv(previewId: TID | null, previewV2EnvId: TID | null): Promise<Observable<boolean>>

  cancelTaskProcess(taskId: string): Promise<void>

  checkoutTaskCheckpoint(taskId: string, commitId: string, force: boolean): Promise<TaskCheckpointCheckoutResult>

  commandOutputUpdates(taskId: TID): Promise<Observable<ExecCommandResultUpdated>>

  completePreviewRuntimeSetup(taskId: string): Promise<TID | null>

  configurePreviewRuntime(projectKey: ProjectKey, repo: string): Promise<AiAgentTask>

  continuePreviewRuntimeConfigWithSecrets(taskId: string, secrets: ProjectSecretWithValue[], messageId: string | null): Promise<void>

  continuePreviewRuntimeConfigWithServices(taskId: string, services: InitRuntimeConfigTaskState.Service[]): Promise<void>

  continueTaskProcess(taskId: string, messageId: string): Promise<void>

  createInvitation(email: string, project: ProjectKey | null, role: ProjectTeamRole | null, globalRole?: OrganizationMemberRole | null, landingContext?: InvitationLandingContext | null, userMessage?: string | null): Promise<void>

  createProjectWarmupSnapshot(projectKey: ProjectKey, repoName: string): Promise<WarmupSnapshot>

  createSampleProject(name: string, url: string, firstTaskIdeas: string[], settings: AiAgentTaskSettings | null): Promise<AiAgentTask>

  createTask(projectKey: ProjectKey, repo: string, params: GenericTaskParams): Promise<AiAgentTask>

  deleteProject(projectKey: ProjectKey): Promise<void>

  deleteProjectSecret(projectKey: ProjectKey, key: string): Promise<void>

  deleteProjectWarmupSnapshot(projectKey: ProjectKey, repoName: string, warmupSnapshotId: TID): Promise<void>

  deleteSecretInSetupTask(taskId: string, key: string): Promise<void>

  deleteSshKey(profile: UProfileIdentifier, fingerprint: string): Promise<void>

  deleteTask(taskId: string): Promise<void>

  disconnectFigmaAccount(): Promise<void>

  downloadAttachment(taskId: TID, attachmentId: TID): Promise<Blob>

  downloadDomElementScreenshot(taskId: TID, blobId: TID): Promise<string>

  downloadFile(taskId: TID, attachmentId: TID): void

  downloadPatch(taskId: TID): void

  downloadPreviewEnvLogs(taskId: TID): void

  editFiles(projectKey: ProjectKey, repo: string, baseCommit: string, targetBranch: string, commitMessage: string, files: GitCommitFileRequest[]): Promise<GitCommitResult>

  editMessage(messageId: string, text: string, context: AiAgentTaskContextItem[], attachments: string[], elements: AiAgentTaskDomElementIn[], dropNewerVersions?: boolean | null): Promise<UTaskCheckpointCheckoutResult>

  favoriteProjects(): Promise<ProjectKey[]>

  findFiles(projectKey: ProjectKey, repo: string, commit: string | null, pattern: string, batchInfo: BatchInfo): Promise<Batch<GitFile>>

  getBranches(batchInfo: BatchInfo, project: ProjectKey, repo: string, query: string | null): Promise<Batch<BranchInfo>>

  getCommitChanges(batchInfo: BatchInfo, projectKey: ProjectKey, repoName: string, commits: string[]): Promise<GitCommitChange[]>

  getExecCommandResult(execResultId: TID, skipStdOut: number, skipStdErr: number): Promise<ExecCommandResult>

  getFetchStatus(projectKey: ProjectKey): Promise<RepositoryFetchStatus>

  getFileContentBytes(projectKey: ProjectKey, repo: string, blobId: string, skip: number, limit: number): Promise<FileContentPart>

  getGitHubConfigurationUrl(projectKey: ProjectKey, repoName: string): Promise<string>

  getGitHubStatus(projectKey: ProjectKey, repoName: string): Promise<InitializedChannel<GitHubStatus, GitHubStatus>>

  getHost(): string

  getInlineDiff(project: ProjectKey, repoName: string, file: GitCommitChange): Promise<InlineDiff | null>

  getLlmProfileIds(): Promise<GrazieLLMProfileDTO[]>

  getOrgQuota(): Promise<LlmQuota>

  getOrganizationMembers(batchInfo: BatchInfo): Promise<Batch<OrganizationMember>>

  getOrganizationPermissions(): Promise<OrganizationPermissions>

  getPooledPreview(projectKey: ProjectKey, repositoryName: string): Promise<TID | null>

  getPrShortSummary(taskId: string): Promise<string | null>

  getProfilePreference(key: string): Promise<string | null>

  getProjectCodingGuidelines(projectKey: ProjectKey, repository: string): Promise<ProjectCodingGuidelines | null>

  getProjectMembers(batchInfo: BatchInfo, projectKey: ProjectKey): Promise<Batch<Pair<TD_MemberProfile, boolean>>>

  getProjectPermissions(projectKey: ProjectKey): Promise<ProjectPermissions>

  getProjectRuntimeConfig(projectKey: ProjectKey, repository: string): Promise<Pair<RuntimeConfig, boolean> | null>

  getProjectSettings(projectKey: ProjectKey): Promise<ProjectSettings>

  getProjectV2(projectKey: ProjectKey): Promise<PR_Project2>

  getRepositoryAuthOptions(url: string): Promise<RepositoryAuthType[]>

  getRuntimeEnvironmentDescription(taskId: string): Promise<string>

  getSlackNotificationsEnabled(): Promise<boolean>

  getSshConnectCommandForPreview(previewId: TID | null, previewV2EnvId: TID | null): Promise<string>

  getSshTunnelCommandForPreview(previewId: TID | null, previewV2Id: TID | null): Promise<string>

  getTaskGuidelines(taskId: string): Promise<ProjectCodingGuidelines | null>

  getTaskStats(taskId: string): Promise<InitializedChannel<DevTaskStats, DevTaskStats>>

  getWarmupInfo(projectKey: ProjectKey, repository: string): Promise<WarmupInfo>

  installRemoteMCP(name: string, host: string): Promise<string>

  inviteMatterSupport(message: string, taskId: string | null): Promise<void>

  listFiles(projectKey: ProjectKey, repo: string, commit: string | null, path: string | null, file: string | null, options: ListFilesOptions[], limit: number | null): Promise<GitFile[]>

  listPreviewSetupTasks(projectKey: ProjectKey, repository: string, batchInfo: BatchInfo): Promise<Batch<AiAgentTask>>

  listProfiles(batchInfo: BatchInfo, query: string): Promise<TD_MemberProfile[]>

  listProjectSecrets(projectKey: ProjectKey): Promise<ProjectSecret[]>

  listProjectWarmupSnapshots(projectKey: ProjectKey, repoName: string): Promise<WarmupSnapshot[]>

  listProjectsV2(batchInfo: BatchInfo, numberOfTasks: number): Promise<Batch<PR_Project2>>

  listSshKeys(profile: UProfileIdentifier): Promise<SshKeyData[]>

  listTasks(projectKey: ProjectKey, repo: string, principalId: string | null, successful: boolean, withCollaborators: boolean, batchInfo: BatchInfo): Promise<Batch<AiAgentTask>>

  listTasksForUser(userId: string, successful: boolean, batchInfo: BatchInfo): Promise<Batch<AiAgentTask>>

  liveUpdatesDebugEvents(): Promise<Observable<AppDateTime>>

  llmLog(taskId: string, pageId: string | null, batchInfo: BatchInfo, sorting: MessagesSorting): Promise<Batch<LLMLogEntry>>

  llmLogPageUpdates(taskId: string, pageId: string | null): Promise<Observable<LLMLogPage>>

  llmLogPages(taskId: string, batchInfo: BatchInfo, sorting: MessagesSorting): Promise<Batch<LLMLogPage>>

  llmLogRequestContext(logEntryId: string): Promise<GrazieLLMRequest>

  llmLogUpdates(taskId: string, pageId: string | null): Promise<Observable<LLMLogEntry>>

  markAsSuccessful(taskId: string, commitId: string, successful: boolean): Promise<void>

  me(): Promise<CPrincipal>

  message(messageId: string): Promise<AiChatMessage>

  messageUpdates(taskId: string): Promise<Observable<AiChatEvent>>

  messages(taskId: string, batchInfo: BatchInfo, sorting: MessagesSorting): Promise<Batch<AiChatMessage>>

  openPrInGitHub(taskId: string, branchName: string, title: string, description: string | null, shortSummary: string | null): Promise<PrCreationResult>

  profile(profile: ProfileIdentifier): Promise<TD_MemberProfile>

  putUserKeysIntoPreviewEnv(id: string | null, v2EnvId: string | null): Promise<SshKeyReportData[]>

  readMessage(messageId: string): Promise<void>

  recreatePreview(id: string): Promise<void>

  removeOrganizationMember(profileId: TID): Promise<void>

  removePreviewCommand(taskId: string, commandId: string): Promise<void>

  removeProjectMember(projectKey: ProjectKey, userId: string, role: ProjectTeamRole): Promise<void>

  reportCreatePullRequestClickedEvent(taskId: string): Promise<void>

  reportTaskDraftGeneratedEvent(taskId: string): Promise<void>

  resetProjectCreationFlow(taskId: string, stage: ProjectCreationBreadcrumb): Promise<void>

  resetSlackToken(): Promise<void>

  resolvePrincipals(pids: string[]): Promise<Pair<string, CPrincipal>[]>

  restartPreview(id: string): Promise<void>

  retryTaskProcess(taskId: string, messageId: string): Promise<void>

  saveBeforeCommand(taskId: string, script: string): Promise<void>

  savePreviewCommand(taskId: string, commandId: string, script: string): Promise<void>

  savePreviewPort(taskId: string, port: number): Promise<void>

  sendMessage(taskId: string, text: string, context: AiAgentTaskContextItem[], attachments: string[], elements: AiAgentTaskDomElementIn[]): Promise<string>

  sendTypingEvent(taskId: string, stop: boolean): Promise<void>

  setOrganizationMemberRole(profileId: TID, role: OrganizationMemberRole): Promise<void>

  setProfilePreference(key: string, value: string | null): Promise<void>

  setProjectMemberRole(projectKey: ProjectKey, userId: string, role: ProjectTeamRole): Promise<void>

  setProjectName(taskId: string, name: string, createEmptyProject: boolean): Promise<void>

  setRepositoryAccess(taskId: string, access: RepositoryAuthType): Promise<void>

  setRepositoryAuth(taskId: string, remote: URemoteRepositoryAuth): Promise<void>

  setRepositoryUrl(taskId: string, url: string): Promise<void>

  showPersonalTokenHelp(taskId: string): Promise<void>

  startFetch(projectKey: ProjectKey): Promise<void>

  startFigmaOAuthFlow(): Promise<string | null>

  startGitHubOAuthFlow(): Promise<string>

  startProjectCreation(url: string, settings: AiAgentTaskSettings | null): Promise<AiAgentTask>

  startSlackUserForNotificationsOAuthFlow(): Promise<string>

  stopBeforeCommand(taskId: string): Promise<void>

  stopPreviewCommand(taskId: string, commandId: string): Promise<void>

  subscribeToFigmaAuthStatus(): Promise<InitializedChannel<FigmaStatus, FigmaStatus>>

  subscribeToOpenedPullRequests(taskIds: string[]): Promise<InitializedChannel<Pair<TID, GitHubPrInfo[]>[], Pair<TID, GitHubPrInfo[]>[]>>

  subscribeToPreviewAppLogs(id: TID | null, v2EnvId: TID | null, logId: string): Promise<Observable<string>>

  subscribeToPreviewLogList(id: TID | null, previewV2Id: TID | null): Promise<Observable<AvailableLog[]>>

  subscribeToPreviewProcessState(previewId: TID | null, previewV2Id: TID | null): Promise<Observable<UPreviewProcessesApiState>>

  subscribeToPreviewReadyEstimate(previewId: TID): Promise<Observable<number>>

  subscribeToRepositorySyncErrors(projectKey: ProjectKey, repository: string): Promise<InitializedChannel<RepositoryFetchStatus, RepositoryFetchStatus>>

  subscribeToTypingEvents(taskId: TID): Promise<Observable<TypingEventDTO>>

  task(taskId: string): Promise<AiAgentTask>

  taskDetails(taskId: string, orgId?: string, projectKey?: string): Promise<AiAgentTaskDetails>

  taskUpdates(taskId: string): Promise<Observable<AiAgentTaskEvent>>

  toggleFavoriteProject(projectKey: ProjectKey, favorite: boolean): Promise<void>

  updateProjectCodingGuidelines(projectKey: ProjectKey, repository: string, content: string): Promise<void>

  updateProjectColor(projectKey: ProjectKey, color: ProjectColorKey | null): Promise<void>

  updateProjectCover(projectKey: ProjectKey, attachmentId: TID | null): Promise<void>

  updateProjectName(projectKey: ProjectKey, name: string): Promise<void>

  updateProjectRootFolder(projectKey: ProjectKey, repository: string, rootFolder: string): Promise<void>

  updateProjectSecret(projectKey: ProjectKey, key: string, description: string | null, value: string): Promise<void>

  updateProjectSettings(projectKey: ProjectKey, settings: ProjectSettings): Promise<void>

  updateRepositoryAuth(projectKey: ProjectKey, repository: string, remoteUrl: string, auth: URemoteRepositoryAuth): Promise<void>

  updateSecretInSetupTask(taskId: string, secret: ProjectSecretWithValue): Promise<void>

  updateTaskGuidelines(taskId: string, content: string): Promise<void>

  updateTaskSettings(taskId: string, settings: AiAgentTaskSettings): Promise<void>

  updateTaskTitle(taskId: string, title: string): Promise<void>

  uploadFile(file: File): Promise<AiChatMessageAttachment>

  validateRemoteUrl(url: string): Promise<RemoteUrlValidationResult>

  verifyBeforeCommand(taskId: string): Promise<void>

  verifyPreviewCommand(taskId: string, commandId: string): Promise<void>
}

async function createApiService(wsUrl: string, tokenSource: TokenSource): Promise<ApiService> {
  return new Promise<ApiService>(function (resolve, reject) {
    const service = new ApiService(wsUrl, tokenSource)
    service.on("status", (status: ConnectionStatus) => {
      switch (status) {
        case ConnectionStatus.Connected:
          resolve(service)
          break
        case ConnectionStatus.AuthFailed:
          reject(new AuthInOrgFailed())
          break
        case ConnectionStatus.ConnectFailed:
        case ConnectionStatus.Closed:
          reject(new ConnectToOrgFailed(null))
          break
      }
    })
  })
}

const httpsRegex = /^https:\/\//
const httpRegex = /^http:\/\//

function asWsEndpointUrl(host: string, orgId: string): string {
  const wsHost = host.replace(httpsRegex, "wss://").replace(httpRegex, "ws://")
  return trimEnd(wsHost, "/") + `/${orgId}` + "/api/v1/ws"
}

type OrgInfo = {
  exists: boolean
  active: boolean
  host: string
}

export async function getDomicileBackendHost(host: string, orgId: string): Promise<string> {
  const res = await fetch(`${host}/orgs/find?org=${orgId}`, {
    mode: "cors",
  })

  if (!res.ok) {
    throw new OrgNotFoundException(orgId)
  }

  const orgInfo = await res.json() as OrgInfo

  if (!orgInfo.exists || !orgInfo.active) {
    throw new OrgNotFoundException(orgId)
  }

  return orgInfo.host
}

class ConnectToOrgFailed extends Error {
  constructor(readonly domicileRegion: string | null) {
    super()
  }
}

class AuthInOrgFailed extends Error {
  constructor() {
    super()
  }
}

async function getLimitedOrgToken(host: string, orgId: string, tokenSource: TokenSource): Promise<string> {
  let res: Response
  try {
    res = await fetch(`${host}/${orgId}/auth/limited-token`, {
      mode: "cors",
      headers: {
        authorization: (await tokenSource.token()).accessToken,
      },
    })
  } catch {
    throw new ConnectToOrgFailed(null)
  }

  if (!res.ok) {
    if (res.status === 401) {
      throw new AuthInOrgFailed()
    }

    const regionHost = res.headers.get("X-Darkmatter-Region")
    throw new ConnectToOrgFailed(regionHost)
  }

  return (await res.blob()).text()
}

export async function createApiClient(
  host: string,
  orgId: string,
  userInfo: UserInfo | null,
  tokenSource: TokenSource
): Promise<ApiClient> {
  // Connect to the default API backend
  let domicileHost: string | null = null
  try {
    const limitedToken = await getLimitedOrgToken(trimEnd(host, "/"), orgId, tokenSource)
    const service = await createApiService(asWsEndpointUrl(host, orgId), tokenSource)
    return new ApiClientImpl(trimEnd(host, "/") + `/${orgId}`, orgId, new LimitedTokenSource(limitedToken), service)
  } catch (err) {
    if (err instanceof AuthInOrgFailed) {
      throw new UserInOrgNotFoundException(userInfo, orgId)
    } else if (err instanceof ConnectToOrgFailed) {
      domicileHost = err.domicileRegion
    } else {
      throw err
    }
  }

  // Are we in roaming?
  if (domicileHost == null) {
    domicileHost = await getDomicileBackendHost(host, orgId)
  }

  try {
    const limitedToken = await getLimitedOrgToken(trimEnd(domicileHost, "/"), orgId, tokenSource)
    const service = await createApiService(asWsEndpointUrl(domicileHost, orgId), tokenSource)
    return new ApiClientImpl(trimEnd(domicileHost, "/") + `/${orgId}`, orgId, new LimitedTokenSource(limitedToken), service)
  } catch (err) {
    if (err instanceof AuthInOrgFailed) {
      throw new UserInOrgNotFoundException(userInfo, orgId)
    }
    // ConnectionStatus.ConnectFailed means internal server error here
    throw err
  }
}

class LimitedTokenSource implements TokenSource {
  constructor(private readonly _token: string) {
  }

  async token() {
    return new TokenInfo(this._token, Date.now() + (60 * 60 * 24 * 30) * 1000) // 1 month
  }

  onTokenChanged(_: (token: TokenInfo) => void) {
  }
}

class ApiClientImpl implements ApiClient {
  constructor(private readonly host: string, private readonly orgId: string, private readonly limitedToken: TokenSource, service: ApiService) {
    this.aiAgent = new AiAgentSpaceApiClient(service)
    this.aiChat = new AiChatApiClient(service)
    this.codeView = new CodeViewServiceClient(service)
    this.initRuntimeConfig = new InitRuntimeConfigApiClient(service)
    this.invitations = new InvitationsClient(service)
    this.preview = new PreviewApiClient(service)
    this.profiles = new ProfilesClient(service)
    this.projects = new ProjectsClient(service)
    this.sshKeys = new SshKeysClient(service)
    this.initProject = new InitProjectApiClient(service)

    void this.profiles.getProfilePreference(DEV_MODE_PREFERENCE_KEY).then(value => {
      this.devMode = value === "true"
    }).catch(() => {
      this.devMode = false
    })
  }

  private readonly aiAgent: AiAgentSpaceApiClient
  private readonly aiChat: AiChatApiClient
  private readonly codeView: CodeViewServiceClient
  private readonly initRuntimeConfig: InitRuntimeConfigApiClient
  private readonly invitations: InvitationsClient
  private readonly preview: PreviewApiClient
  private readonly profiles: ProfilesClient
  private readonly projects: ProjectsClient
  private readonly sshKeys: SshKeysClient
  private readonly initProject: InitProjectApiClient

  // This property is used a lot, so we cache it locally
  private devMode = false

  async addPreviewCommand(taskId: string, id: string, title: string, script: string) {
    return await this.withErrorToast(this.initRuntimeConfig.addPreviewCommand(taskId as TID, id, title, script))
  }

  async addProjectSecret(projectKey: ProjectKey, secret: ProjectSecret, value: string) {
    return await this.withErrorToast(this.projects.addProjectSecret(projectKey, secret, value))
  }

  async addSshKey(profile: UProfileIdentifier, key: string, comment: string) {
    return await this.withErrorToast(this.sshKeys.addSshKey(profile, key, comment))
  }

  async archivedMessages(taskId: string, batchInfo: BatchInfo, blockMessageIds: string[], sorting: MessagesSorting) {
    return await this.withErrorToast(this.aiChat.archivedMessages(taskId as TID, blockMessageIds as TID[], batchInfo, sorting))
  }

  buildStaticUrl(path: string) {
    const url = new URL(path, this.getHost())
    if (this.orgId) {
      url.pathname = "/" + this.orgId + url.pathname
    }

    return url.toString()
  }

  async canSshToPreviewEnv(previewId: TID | null, previewV2EnvId: TID | null) {
    return await this.withErrorToast(this.preview.canSshToPreviewEnv(previewId, previewV2EnvId))
  }

  async cancelTaskProcess(taskId: string) {
    return await this.withErrorToast(this.aiAgent.cancelTaskProcess(taskId as TID))
  }

  async checkoutTaskCheckpoint(taskId: string, commitId: string, force: boolean) {
    return await this.withErrorToast(this.aiAgent.checkoutTaskCheckpoint(taskId as TID, commitId, force))
  }

  async commandOutputUpdates(taskId: TID) {
    return await this.withErrorToast(this.aiChat.commandOutputUpdates(taskId))
  }

  async getExecCommandResult(execResultId: TID, skipStdOut: number, skipStdErr: number) {
    return await this.withErrorToast(this.aiChat.getExecCommandResult(execResultId, skipStdOut, skipStdErr))
  }

  async completePreviewRuntimeSetup(taskId: string) {
    return await this.withErrorToast(this.initRuntimeConfig.complete(taskId as TID))
  }

  async configurePreviewRuntime(projectKey: ProjectKey, repo: string) {
    const taskId = await this.withErrorToast(this.initRuntimeConfig.configurePreviewRuntime(projectKey, repo))
    return (await this.taskDetails(taskId)).task
  }

  async continuePreviewRuntimeConfigWithSecrets(taskId: string, secrets: ProjectSecretWithValue[], messageId: string | null) {
    return await this.withErrorToast(this.initRuntimeConfig.continuePreviewRuntimeConfigWithSecrets(taskId as TID, secrets, messageId as TID | null))
  }

  async continuePreviewRuntimeConfigWithServices(taskId: string, services: InitRuntimeConfigTaskState.Service[]) {
    return await this.withErrorToast(this.initRuntimeConfig.continuePreviewRuntimeConfigWithServices(taskId as TID, services))
  }

  async continueTaskProcess(taskId: string, messageId: string) {
    return await this.withErrorToast(this.aiAgent.continueTaskProcess(taskId as TID, messageId as TID))
  }

  async createInvitation(email: string, projectKey: ProjectKey | null, role: ProjectTeamRole | null, globalRole?: OrganizationMemberRole | null, landingContext?: InvitationLandingContext | null, userMessage?: string | null) {
    return await this.withErrorToast(this.invitations.createInvitation(email, projectKey, role, globalRole ?? null, landingContext ?? null, userMessage ?? null))
  }

  async createProjectWarmupSnapshot(projectKey: ProjectKey, repoName: string) {
    return await this.withErrorToast(this.preview.createProjectWarmupSnapshot(projectKey, repoName))
  }

  async createSampleProject(name: string, url: string, firstTaskIdeas: string[], settings: AiAgentTaskSettings | null) {
    return await this.withErrorToast(this.projects.createSampleProject(name, url, firstTaskIdeas, settings))
  }

  async createTask(projectKey: ProjectKey, repo: string, params: GenericTaskParams) {
    const taskId = await this.withErrorToast(this.aiAgent.createGenericTask(
      projectKey,
      repo,
      params.prompt,
      params.initialBranchInfo || null,
      params.settings,
      params.contextItems || [],
      params.attachments || [],
      params.elements || [],
      null
    ))

    return (await this.taskDetails(taskId)).task
  }

  async deleteProject(projectKey: ProjectKey) {
    return await this.withErrorToast(this.projects.deleteProject(projectKey))
  }

  async deleteProjectSecret(projectKey: ProjectKey, key: string) {
    return await this.withErrorToast(this.projects.deleteProjectSecret(projectKey, key))
  }

  async deleteProjectWarmupSnapshot(projectKey: ProjectKey, repoName: string, warmupSnapshotId: TID) {
    return await this.withErrorToast(this.preview.deleteProjectWarmupSnapshot(projectKey, repoName, warmupSnapshotId))
  }

  async deleteSecretInSetupTask(taskId: string, key: string) {
    return await this.withErrorToast(this.initRuntimeConfig.deleteSecretInSetupTask(taskId as TID, key))
  }

  async deleteSshKey(profile: UProfileIdentifier, fingerprint: string) {
    return await this.withErrorToast(this.sshKeys.deleteSshKey(profile, fingerprint))
  }

  async deleteTask(taskId: string) {
    return await this.withErrorToast(this.aiAgent.deleteTask(taskId as TID))
  }

  async disconnectFigmaAccount() {
    return await this.withErrorToast(this.aiAgent.disconnectFigmaAccount())
  }

  async downloadAttachment(taskId: TID, attachmentId: TID) {
    const url = `${this.getHost()}/attachments/${taskId}/${attachmentId}`

    const ret = await fetch(url, {
      mode: "cors",
      headers: {
        authorization: `Bearer ${(await this.limitedToken.token()).accessToken}`
      }
    })

    if (!ret.ok) {
      const err = `Failed to download attachment: ${ret.statusText}`
      toast.error(err)
      log.error(err)
    }

    return await ret.blob()
  }

  async downloadDomElementScreenshot(taskId: TID, blobId: TID) {
    const url = `${this.getHost()}/dom-element-screenshot/${taskId}/${blobId}`

    const ret = await fetch(url, {
      mode: "cors",
      headers: {
        authorization: `Bearer ${(await this.limitedToken.token()).accessToken}`
      }
    })

    if (!ret.ok) {
      const err = `Failed to download screenshot: ${ret.statusText}`
      toast.error(err)
      log.error(err)
    }

    return window.URL.createObjectURL(await ret.blob())
  }

  downloadFile(taskId: TID, attachmentId: TID) {
    this.downloadUrl(`${this.getHost()}/attachments/${taskId}/${attachmentId}`)
  }

  downloadPatch(taskId: TID) {
    this.downloadUrl(`${this.getHost()}/patch/${taskId}`)
  }

  downloadPreviewEnvLogs(taskId: TID) {
    this.downloadUrl(`${this.getHost()}/task/${taskId}/preview-env-logs`)
  }

  async editFiles(projectKey: ProjectKey, repo: string, baseCommit: string, targetBranch: string, commitMessage: string, files: GitCommitFileRequest[]) {
    return await this.withErrorToast(this.codeView.editFiles(projectKey, repo, baseCommit, targetBranch, commitMessage, files))
  }

  async editMessage(messageId: string, text: string, context: AiAgentTaskContextItem[], attachments: string[], elements: AiAgentTaskDomElementIn[], dropNewerVersions: boolean | null = null) {
    return await this.withErrorToast(this.aiChat.editMessage(messageId as TID, text, context, attachments as TID[], elements, dropNewerVersions))
  }

  async favoriteProjects() {
    return await this.withErrorToast(this.projects.favoriteProjects())
  }

  async findFiles(projectKey: ProjectKey, repo: string, commit: string | null, pattern: string, batchInfo: BatchInfo) {
    return await this.withErrorToast(this.codeView.findFiles(projectKey, repo, commit, pattern, batchInfo))
  }

  async getBranches(batchInfo: BatchInfo, project: ProjectKey, repo: string, query: string | null) {
    return await this.withErrorToast(this.codeView.getBranches(batchInfo, project, repo, query || ""))
  }

  async getCommitChanges(batchInfo: BatchInfo, projectKey: ProjectKey, repoName: string, commits: string[]) {
    return (await this.withErrorToast(this.codeView.getCommitChanges(batchInfo, projectKey, repoName, commits))).data
  }

  async getFetchStatus(projectKey: ProjectKey) {
    return await this.withErrorToast(this.projects.getFetchStatus(projectKey))
  }

  async getFileContentBytes(projectKey: ProjectKey, repo: string, blobId: string, skip: number, limit: number) {
    return await this.withErrorToast(this.codeView.getFileContentBytes(projectKey, repo, blobId, skip, limit))
  }

  async getGitHubConfigurationUrl(projectKey: ProjectKey, repoName: string) {
    return await this.withErrorToast(this.aiAgent.getGitHubConfigurationUrl(projectKey, repoName))
  }

  async getGitHubStatus(projectKey: ProjectKey, repoName: string) {
    return await this.withErrorToast(this.aiAgent.getGitHubStatus(projectKey, repoName))
  }

  getHost() {
    return trimEnd(this.host, "/")
  }

  async getInlineDiff(projectKey: ProjectKey, repoName: string, file: GitCommitChange) {
    return await this.withErrorToast(this.codeView.getInlineDiff(projectKey, repoName, file, false, true, []))
  }

  async getLlmProfileIds() {
    return await this.withErrorToast(this.aiAgent.getLlmProfileIds())
  }

  async getOrgQuota() {
    return await this.withErrorToast(this.aiAgent.getOrgQuota())
  }

  async getOrganizationMembers(batchInfo: BatchInfo) {
    return await this.withErrorToast(this.profiles.listOrganizationMembers(batchInfo))
  }

  async getOrganizationPermissions() {
    return await this.withErrorToast(this.profiles.getOrganizationPermissions())
  }

  async getPooledPreview(projectKey: ProjectKey, repositoryName: string) {
    return await this.withErrorToast(this.preview.getActivePooledPreviewId(projectKey, repositoryName))
  }

  async getPrShortSummary(taskId: string) {
    return await this.withErrorToast(this.aiAgent.getPrShortSummary(taskId))
  }

  async getProfilePreference(key: string) {
    return await this.withErrorToast(this.profiles.getProfilePreference(key))
  }

  async getProjectCodingGuidelines(projectKey: ProjectKey, repository: string) {
    return await this.withErrorToast(this.projects.getProjectCodingGuidelines(projectKey, repository))
  }

  async getProjectMembers(batchInfo: BatchInfo, projectKey: ProjectKey) {
    return await this.withErrorToast(this.projects.getProjectMembers(batchInfo, projectKey))
  }

  async getProjectPermissions(projectKey: ProjectKey) {
    return await this.withErrorToast(this.projects.getProjectPermissions(projectKey))
  }

  async getProjectRuntimeConfig(projectKey: ProjectKey, repository: string) {
    return await this.withErrorToast(this.projects.getProjectRuntimeConfig(projectKey, repository))
  }

  async getProjectSettings(projectKey: ProjectKey) {
    // eslint-disable-next-line @typescript-eslint/no-deprecated
    return await this.withErrorToast(this.projects.getProjectSettings(projectKey))
  }

  async getProjectV2(projectKey: ProjectKey) {
    return await this.withErrorToast(this.projects.getProjectV2(projectKey, 3), (e) => new ProjectNotFoundException(e.message, this.orgId))
  }

  async getRepositoryAuthOptions(url: string): Promise<RepositoryAuthType[]> {
    return await this.withErrorToast(this.initProject.supportedAuthTypes(url))
  }

  async getRuntimeEnvironmentDescription(taskId: string): Promise<string> {
    return await this.withErrorToast(this.initRuntimeConfig.getRuntimeEnvironmentDescription(taskId as TID))
  }

  async getTaskGuidelines(taskId: string): Promise<ProjectCodingGuidelines | null> {
    return await this.withErrorToast(this.initRuntimeConfig.getTaskGuidelines(taskId as TID))
  }

  async getSlackNotificationsEnabled() {
    return await this.withErrorToast(this.aiChat.getSlackNotificationsEnabled())
  }

  async getSshConnectCommandForPreview(previewId: TID | null, previewV2EnvId: TID | null) {
    return await this.withErrorToast(this.preview.getSshConnectCommandForPreview(previewId, previewV2EnvId))
  }

  async getSshTunnelCommandForPreview(previewId: TID | null, previewV2Id: TID | null) {
    return await this.withErrorToast(this.preview.getSshTunnelCommandForPreview(previewId, previewV2Id))
  }

  async getTaskStats(taskId: TID) {
    return await this.withErrorToast(this.aiAgent.getDevTaskStats(taskId))
  }

  async getWarmupInfo(projectKey: ProjectKey, repository: string) {
    return await this.withErrorToast(this.aiAgent.getWarmupInfo(projectKey, repository))
  }

  async installRemoteMCP(name: string, host: string): Promise<string> {
    return await this.withErrorToast(this.aiAgent.installRemoteMCP(name, host, window.location.href))
  }

  async inviteMatterSupport(message: string, taskId: string | null) {
    return await this.withErrorToast(this.invitations.inviteMatterSupport(message, taskId as TID))
  }

  async listFiles(projectKey: ProjectKey, repo: string, commit: string | null, path: string | null, file: string | null, options: ListFilesOptions[], limit: number | null) {
    return await this.withErrorToast(this.codeView.listFiles(projectKey, repo, commit, path, file, options, limit))
  }

  async listPreviewSetupTasks(projectKey: ProjectKey, repository: string, batchInfo: BatchInfo) {
    return await this.withErrorToast(this.aiAgent.listPreviewSetupTasks(projectKey, repository, batchInfo))
  }

  async listProfiles(batchInfo: BatchInfo, query: string) {
    return (await this.withErrorToast(this.profiles.listProfiles(batchInfo, query))).data
  }

  async listProjectSecrets(projectKey: ProjectKey) {
    return await this.withErrorToast(this.projects.listProjectSecrets(projectKey))
  }

  async listProjectWarmupSnapshots(projectKey: ProjectKey, repoName: string) {
    return await this.withErrorToast(this.preview.listProjectWarmupSnapshots(projectKey, repoName))
  }

  async listProjectsV2(batchInfo: BatchInfo, numberOfTasks: number) {
    return await this.withErrorToast(this.projects.listProjectsV2(batchInfo, numberOfTasks))
  }

  async listSshKeys(profile: UProfileIdentifier) {
    return await this.withErrorToast(this.sshKeys.listSshKeys(profile))
  }

  async listTasks(projectKey: ProjectKey, repo: string, principalId: TID | null, successful: boolean, withCollaborators: boolean, batchInfo: BatchInfo) {
    return await this.withErrorToast(this.aiAgent.listTasks(
      projectKey,
      repo,
      principalId,
      null,
      successful,
      withCollaborators,
      batchInfo
    ))
  }

  async listTasksForUser(
    userId: string,
    successful: boolean,
    batchInfo: BatchInfo,
  ) {
    return await this.withErrorToast(this.aiAgent.listTasksForUser(userId as TID, successful, batchInfo))
  }

  async liveUpdatesDebugEvents() {
    return await this.withErrorToast(this.aiAgent.liveUpdateDebugEvents())
  }

  async llmLog(taskId: string, pageId: string | null, batchInfo: BatchInfo, sorting: MessagesSorting) {
    return await this.withErrorToast(this.aiAgent.llmLog(taskId as TID, pageId as TID | null, batchInfo, sorting))
  }

  async llmLogPageUpdates(taskId: string, _pageId: string | null) {
    return await this.withErrorToast(this.aiAgent.llmLogPageUpdates(taskId as TID))
  }

  async llmLogPages(taskId: string, batchInfo: BatchInfo, sorting: MessagesSorting) {
    return await this.withErrorToast(this.aiAgent.llmLogPages(taskId as TID, batchInfo, sorting))
  }

  async llmLogRequestContext(logEntryId: string) {
    return await this.withErrorToast(this.aiAgent.llmLogRequestContext(logEntryId as TID))
  }

  async llmLogUpdates(taskId: string, pageId: string | null) {
    return await this.withErrorToast(this.aiAgent.llmLogUpdates(taskId as TID, pageId as TID | null))
  }

  async markAsSuccessful(taskId: string, commitId: string, successful: boolean) {
    return await this.withErrorToast(this.aiAgent.markAsSuccessful(taskId as TID, commitId as TID, successful))
  }

  async me() {
    return await this.withErrorToast(this.profiles.me())
  }

  async message(messageId: string) {
    return await this.withErrorToast(this.aiChat.message(messageId as TID))
  }

  async messageUpdates(taskId: string) {
    return await this.withErrorToast(this.aiChat.messageUpdates(taskId as TID))
  }

  async messages(taskId: string, batchInfo: BatchInfo, sorting: MessagesSorting) {
    return await this.withErrorToast(this.aiChat.messages(taskId as TID, batchInfo, sorting))
  }

  async openPrInGitHub(taskId: string, branchName: string, title: string, description: string, shortSummary: string | null) {
    return await this.withErrorToast(this.aiAgent.createPullRequest(taskId, branchName, title, description, shortSummary))
  }

  async profile(profile: UProfileIdentifier) {
    return await this.withErrorToast(this.profiles.profile(profile))
  }

  async putUserKeysIntoPreviewEnv(id: string | null, v2EnvId: string | null) {
    return await this.withErrorToast(this.preview.putUserKeysIntoPreviewEnv(id as TID | null, v2EnvId as TID | null))
  }

  async readMessage(messageId: string) {
    return await this.withErrorToast(this.aiChat.read(messageId as TID))
  }

  async recreatePreview(id: string) {
    return await this.withErrorToast(this.aiAgent.recreatePreview(id as TID))
  }

  async removeOrganizationMember(profileId: TID) {
    return await this.withErrorToast(this.profiles.removeOrganizationMember(profileId))
  }

  async removePreviewCommand(taskId: string, commandId: string) {
    return await this.withErrorToast(this.initRuntimeConfig.removePreviewCommand(taskId as TID, commandId))
  }

  async removeProjectMember(projectKey: ProjectKey, userId: string, role: ProjectTeamRole) {
    return await this.withErrorToast(this.projects.removeProjectMember(projectKey, userId, role))
  }

  async reportCreatePullRequestClickedEvent(taskId: string): Promise<void> {
    return await this.withErrorToast(this.aiAgent.reportCreatePullRequestClickedEvent(taskId as TID))
  }

  async reportTaskDraftGeneratedEvent(taskId: string): Promise<void> {
    return await this.withErrorToast(this.aiAgent.reportTaskDraftGeneratedEvent(taskId as TID))
  }

  async resetProjectCreationFlow(taskId: string, stage: ProjectCreationBreadcrumb) {
    return await this.withErrorToast(this.initProject.resetProjectCreationFlow(taskId as TID, stage))
  }

  async resetSlackToken() {
    return await this.withErrorToast(this.aiChat.resetSlackToken())
  }

  async resolvePrincipals(pids: TID[]) {
    return await this.withErrorToast(this.profiles.resolvePrincipals(pids))
  }

  async restartPreview(id: string) {
    return await this.withErrorToast(this.aiAgent.restartPreview(id as TID))
  }

  async retryTaskProcess(taskId: string, messageId: string) {
    return await this.withErrorToast(this.aiAgent.retryTaskProcess(taskId as TID, messageId as TID))
  }

  async saveBeforeCommand(taskId: string, script: string) {
    return await this.withErrorToast(this.initRuntimeConfig.saveBeforeCommand(taskId as TID, script))
  }

  async savePreviewCommand(taskId: string, commandId: string, script: string) {
    return await this.withErrorToast(this.initRuntimeConfig.savePreviewCommand(taskId as TID, commandId, script))
  }

  async savePreviewPort(taskId: string, port: number) {
    return await this.withErrorToast(this.initRuntimeConfig.savePreviewPort(taskId as TID, port))
  }

  async sendMessage(taskId: string, text: string, context: AiAgentTaskContextItem[], attachments: string[], elements: AiAgentTaskDomElementIn[]) {
    return await this.withErrorToast(this.aiChat.sendMessage(taskId as TID, text, context, attachments as TID[], elements, false))
  }

  async sendTypingEvent(taskId: string, stop: boolean) {
    return await this.withErrorToast(this.aiChat.sendTypingEvent(taskId as TID, stop))
  }

  async setOrganizationMemberRole(profileId: TID, role: OrganizationMemberRole) {
    return await this.withErrorToast(this.profiles.setOrganizationMemberRole(profileId, role))
  }

  async setProfilePreference(key: string, value: string | null) {
    const result = await this.withErrorToast(this.profiles.setProfilePreference(key, value))
    if (key === DEV_MODE_PREFERENCE_KEY) {
      this.devMode = value === "true"
    }
    return result
  }

  async setProjectMemberRole(projectKey: ProjectKey, userId: string, role: ProjectTeamRole) {
    return await this.withErrorToast(this.projects.setProjectMemberRole(projectKey, userId, role))
  }

  async setProjectName(taskId: string, name: string, createEmptyProject: boolean) {
    return await this.withErrorToast(this.initProject.setProjectName(taskId as TID, name, createEmptyProject))
  }

  async setRepositoryAccess(taskId: string, access: RepositoryAuthType) {
    return await this.withErrorToast(this.initProject.setRepositoryAccess(taskId as TID, access))
  }

  async setRepositoryAuth(taskId: string, remote: URemoteRepositoryAuth) {
    return await this.withErrorToast(this.initProject.setRepositoryAuth(taskId as TID, remote))
  }

  async setRepositoryUrl(taskId: string, url: string) {
    return await this.withErrorToast(this.initProject.setRepositoryUrl(taskId as TID, url))
  }

  async showPersonalTokenHelp(taskId: string) {
    return await this.withErrorToast(this.initProject.showPersonalTokenHelp(taskId as TID))
  }

  async startFetch(projectKey: ProjectKey) {
    return await this.withErrorToast(this.projects.fetchRepository(projectKey))
  }

  async startFigmaOAuthFlow() {
    const url = await this.withErrorToast(this.aiAgent.startFigmaOAuthFlow(window.location.href))
    try {
      if (url) {
        const u = new URL(url)
        const state = u.searchParams.get("state")
        if (state) {
          setSecureStateCookie(FigmaSecureCookie, state)
        }
      }
    } catch (e) {
      log.warn("Couldn't set up a Figma security cookie.", e)
    }
    return url
  }

  async startGitHubOAuthFlow() {
    return await this.withErrorToast(this.aiAgent.startGitHubOAuthFlow(window.location.href))
  }

  async startProjectCreation(url: string, settings: AiAgentTaskSettings | null) {
    return await this.withErrorToast(this.projects.startProjectCreation(url, settings))
  }

  async startSlackUserForNotificationsOAuthFlow() {
    const url = await this.withErrorToast(this.aiChat.startSlackUserForNotificationsOAuthFlow(window.location.href))
    try {
      if (url) {
        const u = new URL(url)
        const state = u.searchParams.get("state")
        if (state) {
          setSecureStateCookie(SlackSecureCookie, state)
        }
      }
    } catch (e) {
      log.warn("Couldn't set up a Slack security cookie.", e)
    }
    return url
  }

  async stopBeforeCommand(taskId: string) {
    return await this.withErrorToast(this.initRuntimeConfig.stopBeforeCommand(taskId as TID))
  }

  async stopPreviewCommand(taskId: string, commandId: string) {
    return await this.withErrorToast(this.initRuntimeConfig.stopPreviewCommand(taskId as TID, commandId))
  }

  async subscribeToFigmaAuthStatus() {
    return await this.withErrorToast(this.aiAgent.subscribeToFigmaAuthStatus())
  }

  async subscribeToOpenedPullRequests(taskIds: string[]) {
    return await this.withErrorToast(this.aiAgent.subscribeToOpenedPullRequests(taskIds))
  }

  async subscribeToPreviewAppLogs(id: TID | null, v2EnvId: TID | null, logId: string) {
    return await this.withErrorToast(this.preview.subscribeToPreviewAppLogs(id, v2EnvId, logId))
  }

  async subscribeToPreviewLogList(id: TID | null, previewV2Id: TID | null) {
    return await this.withErrorToast(this.preview.subscribeToPreviewLogList(id, previewV2Id))
  }

  async subscribeToPreviewProcessState(previewId: TID | null, previewV2Id: TID | null) {
    return await this.withErrorToast(this.preview.subscribeToPreviewProcessState(previewId, previewV2Id))
  }

  async subscribeToPreviewReadyEstimate(previewId: TID) {
    return await this.withErrorToast(this.preview.subscribeToPreviewReadyEstimate(previewId))
  }

  async subscribeToRepositorySyncErrors(projectKey: ProjectKey, repository: string): Promise<InitializedChannel<RepositoryFetchStatus, RepositoryFetchStatus>> {
    return await this.withErrorToast(this.projects.subscribeToRepositorySyncErrors(projectKey, repository))
  }

  async subscribeToTypingEvents(taskId: TID) {
    return await this.withErrorToast(this.aiChat.subscribeToTypingEvents(taskId as TID))
  }

  async task(taskId: string) {
    return await this.withErrorToast(this.aiAgent.task(taskId as TID))
  }

  async taskDetails(id: string, orgId?: string, projectKey?: string) {
    return await this.withErrorToast(this.aiAgent.taskDetails(id as TID), (e) => new TaskNotFoundException(e.message, orgId, projectKey))
  }

  async taskUpdates(taskId: string) {
    return await this.withErrorToast(this.aiAgent.updates(taskId as TID))
  }

  async toggleFavoriteProject(projectKey: ProjectKey, favorite: boolean) {
    return await this.withErrorToast(this.projects.toggleFavoriteProject(projectKey, favorite))
  }

  async updateProjectCodingGuidelines(projectKey: ProjectKey, repository: string, content: string) {
    return await this.withErrorToast(this.projects.updateProjectCodingGuidelines(projectKey, repository, content))
  }

  async updateProjectColor(projectKey: ProjectKey, color: ProjectColorKey | null) {
    return await this.withErrorToast(this.projects.updateProjectColor(projectKey, color))
  }

  async updateProjectCover(projectKey: ProjectKey, attachmentId: TID | null) {
    return await this.withErrorToast(this.projects.updateProjectCover(projectKey, attachmentId))
  }

  async updateProjectName(projectKey: ProjectKey, name: string) {
    return await this.withErrorToast(this.projects.updateProjectName(projectKey, name))
  }

  async updateProjectRootFolder(projectKey: ProjectKey, repository: string, rootFolder: string) {
    return await this.withErrorToast(this.projects.updateProjectRootFolder(projectKey, repository, rootFolder))
  }

  async updateProjectSecret(projectKey: ProjectKey, key: string, description: string | null, value: string) {
    await this.withErrorToast(this.projects.updateProjectSecret(projectKey, key, description, value))
  }

  async updateProjectSettings(projectKey: ProjectKey, settings: ProjectSettings) {
    // eslint-disable-next-line @typescript-eslint/no-deprecated
    return await this.withErrorToast(this.projects.updateProjectSettings(projectKey, settings))
  }

  async updateRepositoryAuth(projectKey: ProjectKey, repository: string, remoteUrl: string, auth: URemoteRepositoryAuth) {
    return await this.withErrorToast(this.projects.setRepositoryAuth(projectKey, repository, remoteUrl, auth))
  }

  async updateSecretInSetupTask(taskId: string, secret: ProjectSecretWithValue): Promise<void> {
    await this.withErrorToast(this.initRuntimeConfig.updateSecretInSetupTask(taskId as TID, secret))
  }

  async updateTaskSettings(taskId: string, settings: AiAgentTaskSettings) {
    return await this.withErrorToast(this.aiAgent.updateTaskSettings(taskId as TID, settings))
  }

  async updateTaskGuidelines(taskId: string, content: string) {
    return await this.withErrorToast(this.initRuntimeConfig.updateTaskGuidelines(taskId as TID, content))
  }

  async updateTaskTitle(taskId: string, title: string) {
    return await this.withErrorToast(this.aiAgent.updateTaskTitle(taskId as TID, title))
  }

  async uploadFile(file: File) {
    const res = await this.withErrorToast(fetch(`${this.getHost()}/attachments/_upload?filename=${file.name}`, {
      method: "POST",
      credentials: "include",
      mode: "cors",
      headers: {
        authorization: `Bearer ${(await this.limitedToken.token()).accessToken}`,
        "Content-Type": file.type || "application/octet-stream",
      },
      body: file,
    }))

    if (!res.ok) {
      const err = `Failed to upload file: ${res.statusText}`
      toast.error(err)
      log.error(err)
    }

    return await this.withErrorToast(res.json()) as AiChatMessageAttachment
  }

  async validateRemoteUrl(url: string) {
    return await this.withErrorToast(this.initProject.validateRemoteUrl(url).catch(() => ({
      valid: true,
      error: null
    })))
  }

  async verifyBeforeCommand(taskId: string) {
    return await this.withErrorToast(this.initRuntimeConfig.verifyBeforeCommand(taskId as TID))
  }

  async verifyPreviewCommand(taskId: string, commandId: string) {
    return await this.withErrorToast(this.initRuntimeConfig.verifyPreviewCommand(taskId as TID, commandId))
  }

  private downloadUrl(url: string) {
    void (async () => {
      const ret = await fetch(url, {
        mode: "cors",
        headers: {
          authorization: `Bearer ${(await this.limitedToken.token()).accessToken}`
        }
      })

      if (!ret.ok) {
        const err = `Failed to download file: ${ret.statusText}`
        toast.error(err)
        log.error(err)
      }

      const objectUrl = window.URL.createObjectURL(await ret.blob())

      const anchor = document.createElement("a")
      document.body.appendChild(anchor)

      anchor.href = objectUrl
      anchor.download = ret.headers.get("Content-Disposition")
        ?.split(";")[1]
        ?.split("=")[1]
        ?.replace(/"/g, "")
        || "file"
      anchor.click()

      window.URL.revokeObjectURL(objectUrl)
      anchor.remove()
    })()
  }

  private async withErrorToast<T>(promise: Promise<T>, errorMapper?: (e: Error) => Error): Promise<T> {
    try {
      return await promise
    } catch (error) {
      const typedError = error as Error
      if (this.devMode) {
        toast.error(typedError.message)
      }
      return Promise.reject(errorMapper ? errorMapper(typedError) : typedError)
    }
  }
}

export function batchInfo(size: number, offset: string | null = null): BatchInfo {
  return { batchSize: size, offset }
}

export function emptyBatch<T>(): Batch<T> {
  return { data: [], next: "0", totalCount: 0 }
}